import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { client2 } from "../../operationalRequest";
import { getDefaultChartOptions } from "../../utils/chartOptions";
import InfoIcon from "../../components/InfoIcon";
import { Spin } from "antd";
import BaseApexChart from "../../components/chart/BaseApexChart";

interface NEOChartProps {
    filter: GraphFilterProps;
    showToolbar?: boolean;
}

interface GraphFilterProps {
    dateType: string;
    date: string;
    siteId?: number;
    systemId?: number;
}

const NEOChart: React.FC<NEOChartProps> = (props) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [options, setOptions] = useState(getDefaultChartOptions({
        showToolbar: props.showToolbar ?? false,
        dateType: props.filter.dateType,
        yAxisTitle: "Normalized Energy Output ( kWh/kW )",
        labelFormatterDecimals: 5,
        tooltipOptions: { shared: true, intersect: false },
    })) 

    const fetchData = async () => {
        setLoading(true);
        const filter = props.filter;
        const params = new URLSearchParams({
            dateType: filter.dateType,
            date: filter.date,
            ...(filter.siteId && { siteId: filter.siteId.toString() }),
            ...(filter.systemId && { systemId: filter.systemId.toString() }),
        });

        const NEOChartDataURL = `/api/dashboard/normalized-energy-output?${params.toString()}`;

        try {
            const response = await client2.get(NEOChartDataURL);

            const formattedData = response.data.data.map((series: any) => ({
                ...series,
                data: series.data.map((point: any) => ({
                    x: point.x,
                    y: point.y,
                })),
            }));

            setData(formattedData);

            const categories =
                formattedData.length > 0
                    ? formattedData[0].data.map((point: any) => point.x)
                    : [];

            setOptions((prevOptions) => ({
                ...prevOptions,
                chart: {
                    ...prevOptions.chart,
                    toolbar: {
                        show: props.showToolbar ?? false,
                    },
                },
                xaxis: {
                    ...prevOptions.xaxis,
                    categories: categories,
                    title: {
                        text:
                            props.filter.dateType === "day"
                                ? "Hour of Day"
                                : "Date",
                    },
                }
            }));
        } catch (error) {
            console.error("Error fetching chart data:", error);
            setData([]);
            setOptions((prevOptions) => ({
                ...prevOptions,
                noData: {
                    ...prevOptions.noData,
                    text: "No data available",
                },
            }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setOptions((prevOptions) => ({
            ...prevOptions,
            chart: {
                ...prevOptions.chart,
            },
            yaxis: {
                ...prevOptions.yaxis
            },
            xaxis: {
                ...prevOptions.xaxis
            },
        }));

        fetchData();
    }, [props.filter]);

    return (
        <div>
            <div className="flex justify-end mb-2">
                <InfoIcon
                    message="Rated capacity is required to visualize normalized energy output."
                    detailedInfo={[
                        "Go to the Device tab from the Sidebar.",
                        "Click on Edit Device Icon.",
                        "Find the Rated Capacity field and add the correct Rated Capacity.",
                    ]}
                />
            </div>
            {loading && (
                <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
                    <Spin size="large" />
                </div>
            )}
            <BaseApexChart 
                key="line"
                type="line"
                options={options as any}
                series={data}
                width="100%"
                height={350}
            />
        </div>
        
    );
};

export default NEOChart;
